import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Center,
  Checkbox,
  Field,
  Flex,
  Input,
  Stack,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { apiFetch, setCurrentUser, type UserInfo } from '../lib/apiFetch'

// `value` is what the server stores and its zod enum accepts; keep in sync with
// FORM_INTERESTS in apps/server/src/routes/auth.ts.
const INTEREST_OPTIONS = [
  { value: 'SDV', labelKey: 'profileForm.interestOption.sdv' },
  { value: 'Digital Twin', labelKey: 'profileForm.interestOption.digitalTwin' },
  { value: 'Auto Test Case Generator', labelKey: 'profileForm.interestOption.autoTest' },
  { value: 'Astemo', labelKey: 'profileForm.interestOption.astemo' },
] as const

// Keep in sync with the zod limits on /auth/request-access and /auth/profile.
const MAX_LENGTH = { name: 100, company: 200, department: 200, email: 254, question: 4000 } as const

interface FieldErrors {
  name?: string
  company?: string
  department?: string
  email?: string
  interests?: string
}

interface ProfileFormProps {
  mode: 'profile' | 'request'
  onComplete: () => void
  onSkip?: () => void
  onBack?: () => void
}

/** The server's English error text is not shown to users; the status decides the message. */
function submitErrorKey(status: number): string {
  if (status === 400) return 'profileForm.errors.invalid'
  if (status === 413) return 'profileForm.errors.tooLarge'
  if (status === 429) return 'profileForm.errors.tooManyRequests'
  return 'profileForm.errors.submitFailed'
}

/** A proxy or load balancer can answer with HTML (502, 504); only parse what is JSON. */
async function readJson<T>(res: Response): Promise<T | null> {
  if (!res.headers.get('content-type')?.includes('application/json')) return null
  try {
    return (await res.json()) as T
  } catch {
    return null
  }
}

export function ProfileForm({ mode, onComplete, onSkip, onBack }: ProfileFormProps) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [company, setCompany] = useState('')
  const [department, setDepartment] = useState('')
  const [email, setEmail] = useState('')
  const [interests, setInterests] = useState<string[]>([])
  const [question, setQuestion] = useState('')
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [submitError, setSubmitError] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const isRequest = mode === 'request'

  function validate(): FieldErrors {
    const errors: FieldErrors = {}
    if (isRequest && !name.trim()) errors.name = t('profileForm.validation.nameRequired')
    if (!company.trim()) errors.company = t('profileForm.validation.companyRequired')
    if (!department.trim()) errors.department = t('profileForm.validation.departmentRequired')
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
    if (!email.trim() || !emailRegex.test(email.trim())) errors.email = t('profileForm.validation.emailInvalid')
    if (interests.length === 0) errors.interests = t('profileForm.validation.interestsRequired')
    return errors
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSubmitError('')

    const errors = validate()
    setFieldErrors(errors)
    if (Object.keys(errors).length > 0) return

    setLoading(true)
    try {
      const endpoint = isRequest ? '/auth/request-access' : '/auth/profile'
      const body: Record<string, unknown> = {
        company: company.trim(),
        department: department.trim(),
        workEmail: email.trim(),
        interests,
        question: question.trim() || undefined,
      }
      if (isRequest) body.name = name.trim()

      const res = await (isRequest
        ? fetch(`/api${endpoint}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          })
        : apiFetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          }))

      if (!res.ok) {
        setSubmitError(t(submitErrorKey(res.status)))
        return
      }

      if (isRequest) {
        setSubmitted(true)
      } else {
        const data = await readJson<{ user?: UserInfo }>(res)
        if (data?.user) setCurrentUser(data.user)
        onComplete()
      }
    } catch {
      setSubmitError(t('profileForm.errors.network'))
    } finally {
      setLoading(false)
    }
  }

  // Clear field error when user starts typing
  const clearError = (field: keyof FieldErrors) => {
    if (fieldErrors[field]) setFieldErrors(prev => ({ ...prev, [field]: undefined }))
  }

  if (submitted) {
    return (
      <Center minH="100vh" bg="gray.900" px={4}>
        <Box
          w="full"
          maxW="520px"
          bg="gray.800"
          borderWidth="1px"
          borderColor="gray.700"
          borderRadius="lg"
          p={6}
        >
          <VStack gap={4} align="stretch">
            <Alert.Root status="success" borderRadius="md" bg="green.800" color="green.100" flexDirection="column" alignItems="flex-start" p={4}>
              <Flex align="center" mb={2}>
                <Alert.Indicator color="green.300" />
                <Text fontWeight="semibold">{t('profileForm.successTitle')}</Text>
              </Flex>
              <Text fontSize="sm">{t('profileForm.success')}</Text>
            </Alert.Root>
            {onBack && (
              <Button variant="outline" colorPalette="gray" onClick={onBack}>
                {t('profileForm.back')}
              </Button>
            )}
          </VStack>
        </Box>
      </Center>
    )
  }

  return (
    <Center minH="100vh" bg="gray.900" px={4} py={8}>
      <Box
        as="form"
        onSubmit={handleSubmit}
        w="full"
        maxW="520px"
        bg="gray.800"
        borderWidth="1px"
        borderColor="gray.700"
        borderRadius="lg"
        p={6}
      >
        <VStack gap={4} align="stretch">
          <Text fontSize="2xl" fontWeight="semibold" color="white">
            {isRequest ? t('profileForm.requestTitle') : t('profileForm.profileTitle')}
          </Text>
          <Text color="gray.300" fontSize="sm">{isRequest ? t('profileForm.requestSubtitle') : t('profileForm.profileSubtitle')}</Text>

          {isRequest && (
            <Field.Root required invalid={!!fieldErrors.name}>
              <Field.Label color="gray.300">{t('profileForm.name')}</Field.Label>
              <Input
                value={name}
                onChange={e => { setName(e.target.value); clearError('name') }}
                maxLength={MAX_LENGTH.name}
                placeholder={t('profileForm.namePlaceholder')}
                bg="gray.700"
                borderColor="gray.600"
                color="white"
              />
              <Field.ErrorText>{fieldErrors.name}</Field.ErrorText>
            </Field.Root>
          )}

          <Field.Root required invalid={!!fieldErrors.company}>
            <Field.Label color="gray.300">{t('profileForm.company')}</Field.Label>
            <Input
              value={company}
              onChange={e => { setCompany(e.target.value); clearError('company') }}
              maxLength={MAX_LENGTH.company}
              placeholder={t('profileForm.companyPlaceholder')}
              bg="gray.700"
              borderColor="gray.600"
              color="white"
            />
            <Field.ErrorText>{fieldErrors.company}</Field.ErrorText>
          </Field.Root>

          <Field.Root required invalid={!!fieldErrors.department}>
            <Field.Label color="gray.300">{t('profileForm.department')}</Field.Label>
            <Input
              value={department}
              onChange={e => { setDepartment(e.target.value); clearError('department') }}
              maxLength={MAX_LENGTH.department}
              placeholder={t('profileForm.departmentPlaceholder')}
              bg="gray.700"
              borderColor="gray.600"
              color="white"
            />
            <Field.ErrorText>{fieldErrors.department}</Field.ErrorText>
          </Field.Root>

          <Field.Root required invalid={!!fieldErrors.email}>
            <Field.Label color="gray.300">{t('profileForm.email')}</Field.Label>
            <Input
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); clearError('email') }}
              maxLength={MAX_LENGTH.email}
              placeholder={t('profileForm.emailPlaceholder')}
              bg="gray.700"
              borderColor="gray.600"
              color="white"
            />
            <Field.ErrorText>{fieldErrors.email}</Field.ErrorText>
          </Field.Root>

          {/* Labeled interest options with required indicator */}
          <Stack gap={1}>
            <Text color="gray.300" fontSize="sm">{t('profileForm.interests')}</Text>
            <Stack gap={2}>
              {INTEREST_OPTIONS.map(opt => (
                <Checkbox.Root
                  key={opt.value}
                  checked={interests.includes(opt.value)}
                  onCheckedChange={({ checked }) => {
                    setInterests(prev => checked ? [...prev, opt.value] : prev.filter(v => v !== opt.value))
                    clearError('interests')
                  }}
                  colorPalette="teal"
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control><Checkbox.Indicator /></Checkbox.Control>
                  <Checkbox.Label color="gray.300">{t(opt.labelKey)}</Checkbox.Label>
                </Checkbox.Root>
              ))}
            </Stack>
            {fieldErrors.interests && <Text color="red.300" fontSize="sm">{fieldErrors.interests}</Text>}
          </Stack>

          <Field.Root>
            <Field.Label color="gray.300">{t('profileForm.question')}</Field.Label>
            <Textarea
              value={question}
              onChange={e => setQuestion(e.target.value)}
              maxLength={MAX_LENGTH.question}
              placeholder={t('profileForm.questionPlaceholder')}
              bg="gray.700"
              borderColor="gray.600"
              color="white"
              rows={3}
            />
          </Field.Root>

          {submitError && <Text color="red.300" fontSize="sm">{submitError}</Text>}

          <Button type="submit" colorPalette="teal" size="lg" loading={loading}>
            {t('profileForm.submit')}
          </Button>

          {/* Visually distinct skip vs back */}
          {onSkip && (
            <Button variant="plain" size="sm" color="whiteAlpha.500" fontWeight="normal" onClick={onSkip}>
              {t('profileForm.skip')}
            </Button>
          )}

          {onBack && (
            <Button variant="outline" size="sm" colorPalette="gray" onClick={onBack}>
              {t('profileForm.back')}
            </Button>
          )}
        </VStack>
      </Box>
    </Center>
  )
}
